import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FolderTree, Building2, UsersRound, Share2, Star, ShieldCheck,
  Search, Bell, LogOut, Globe, Menu, X,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { ThemeToggle } from "./ThemeToggle";
import { BrandMark } from "./AuthLayout";

const NAV = [
  { label: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard },
  { label: "Member Management", to: "/admin/MemberManagement", icon: Users },
  { label: "Category", to: "/admin/Category", icon: FolderTree },
  { label: "Business Directory", to: "/admin/BusinessManagement", icon: Building2 },
  { label: "Family Information", to: "/admin/FamilyInformation", icon: UsersRound },
  { label: "Referral System", to: "/admin/ReferralSystem", icon: Share2 },
  { label: "Review Testimonials", to: "/admin/ReviewTestimonals", icon: Star },
  { label: "Community Admin", to: "/admin/CommunityAdmin", icon: ShieldCheck },
];

/**
 * The nav links, shared by the desktop sidebar and the mobile drawer. Extracted
 * so the two can never drift: previously the drawer did not exist and the
 * sidebar was `lg:flex` only, which left admins on phones and portrait tablets
 * with no navigation at all.
 */
function NavList({ isActive, onNavigate }) {
  return (
    <nav className="flex flex-1 flex-col gap-0.5">
      <span className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">Main</span>
      {NAV.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.to);
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            aria-current={active ? "page" : undefined}
            className={cn(
              "group relative flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-sm font-medium transition-colors duration-150",
              active
                ? "bg-primary/10 text-ink"
                : "text-text hover:bg-line/5 hover:text-ink"
            )}
          >
            {active && (
              <span className="absolute -left-3 top-1/2 h-[16px] w-[2px] -translate-y-1/2 rounded-r bg-brand" />
            )}
            <Icon size={16} className={cn("shrink-0", active ? "text-primary" : "")} />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

/** Admin frame: solid sidebar + solid topbar. Applied once in DashboardLayout. */
export function AppShell({ children, search = true, actions, profileName = "admin", profileRole = "Super Admin" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuButtonRef = React.useRef(null);

  // Close on navigation. Without this the drawer stays open over the page you
  // just moved to, which reads as a broken tap.
  React.useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Escape closes, and focus goes back to the button that opened it rather
  // than being dumped at the top of the document.
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Stop the page behind the drawer scrolling under your finger. Restores the
  // previous value rather than assuming it was "", so this composes with any
  // other component that locks scroll (modals, image cropper).
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [menuOpen]);

  const logout = () => {
    try {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("adminRole");
    } catch {/* no-op */}
    navigate("/admin");
  };

  const isActive = (to) => location.pathname.toLowerCase().startsWith(to.toLowerCase());

  return (
    <div className="relative min-h-screen">
      <div className="relative z-[1] flex min-h-screen">
        {/* Sidebar */}
        <aside
          className="fixed inset-y-0 left-0 z-20 hidden w-[220px] flex-col gap-5 border-r border-line/10 px-3 py-5 lg:flex"
          style={{ background: "var(--sidebar-bg)" }}
        >
          <div className="px-1"><BrandFull /></div>
          <NavList isActive={isActive} />
        </aside>

        {/* Mobile drawer. Below lg the sidebar is hidden, so this is the only
            way to reach any admin page. */}
        <div
          className={cn(
            "fixed inset-0 z-30 bg-black/50 transition-opacity duration-200 lg:hidden",
            menuOpen ? "visible opacity-100" : "invisible opacity-0"
          )}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
        <aside
          id="admin-mobile-nav"
          role="dialog"
          aria-modal="true"
          aria-label="Main navigation"
          className={cn(
            // `invisible` when closed matters for more than looks: an
            // off-screen drawer that is merely translated still holds its links
            // in the tab order, so keyboard focus would vanish off the edge of
            // the screen. visibility:hidden removes them, and because
            // visibility interpolates as a step it stays visible for the whole
            // duration on the way out, so the slide-out still animates.
            "fixed inset-y-0 left-0 z-40 flex w-[260px] max-w-[82vw] flex-col gap-5 border-r border-line/10 px-3 py-5",
            "transition-[transform,visibility] duration-200 ease-out lg:hidden",
            menuOpen ? "visible translate-x-0" : "invisible -translate-x-full"
          )}
          style={{ background: "var(--sidebar-bg)" }}
        >
          <div className="flex items-center justify-between gap-2 px-1">
            <BrandFull />
            <button
              type="button"
              onClick={() => { setMenuOpen(false); menuButtonRef.current?.focus(); }}
              aria-label="Close menu"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-line/15 text-text transition hover:bg-line/5 hover:text-ink"
            >
              <X size={16} />
            </button>
          </div>
          <NavList isActive={isActive} onNavigate={() => setMenuOpen(false)} />
        </aside>

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col lg:ml-[220px]">
          {/* Topbar */}
          <header
            className="sticky top-0 z-[15] flex items-center gap-3 border-b border-line/10 px-4 py-2.5 sm:px-6"
            style={{ background: "var(--topbar-bg)" }}
          >
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="admin-mobile-nav"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-line/15 text-text transition hover:bg-line/5 hover:text-ink lg:hidden"
            >
              <Menu size={18} />
            </button>
            <Link to="/admin/dashboard" className="lg:hidden"><BrandMark /></Link>
            {search && (
              <label className="hidden max-w-[360px] flex-1 items-center gap-2 rounded-md border border-line/15 px-2.5 py-1.5 transition focus-within:border-primary/40 sm:flex"
                     style={{ background: "var(--input-bg)" }}>
                <Search size={15} className="shrink-0 text-muted" />
                <input className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-muted" placeholder="Search members, businesses…" />
              </label>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              {actions}
              {/* Installed as a PWA there is no address bar, so without this an
                  admin cannot get back to the member site. */}
              <Link
                to="/home"
                title="View member site"
                aria-label="View member site"
                className="kbc-control inline-flex h-8 items-center gap-1.5 rounded-md border border-line/15 px-2.5 text-xs font-medium text-text transition-colors duration-150 hover:bg-line/5 hover:text-ink"
              >
                <Globe size={14} />
                <span className="hidden sm:inline">View site</span>
              </Link>
              <ThemeToggle />
              <button className="relative grid h-8 w-8 place-items-center rounded-md border border-line/15 text-text transition hover:bg-line/5 hover:text-ink" aria-label="Notifications">
                <Bell size={16} />
                <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand" />
              </button>
              <div className="flex items-center gap-2 rounded-md border border-line/15 py-1 pl-1 pr-2.5">
                <span className="grid h-6 w-6 place-items-center rounded bg-primary font-display text-xs font-bold text-onprimary">
                  {profileName.charAt(0).toUpperCase()}
                </span>
                <span className="hidden leading-tight sm:block">
                  <span className="block text-xs font-semibold text-ink">{profileName}</span>
                  <span className="block text-[10px] text-muted">{profileRole}</span>
                </span>
              </div>
              <button onClick={logout} className="grid h-8 w-8 place-items-center rounded-md border border-line/15 text-text transition hover:bg-danger/10 hover:text-danger" aria-label="Sign out" title="Sign out">
                <LogOut size={16} />
              </button>
            </div>
          </header>

          <main className="w-full max-w-container p-4 sm:p-6">{children}</main>
        </div>
      </div>
    </div>
  );
}

function BrandFull() {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <span className="grid h-8 w-8 place-items-center rounded-md bg-primary font-display text-sm font-bold text-onprimary">KB</span>
      <span className="flex flex-col leading-tight">
        <span className="font-display text-base font-bold tracking-wide text-ink">KBC</span>
        <span className="text-[10px] text-muted">Kongu Business Community</span>
      </span>
    </div>
  );
}

export default AppShell;
